import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Send, Download, Signature, Ban, RefreshCw, ShieldCheck, AlertTriangle } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABELS = {
  emitted: { label: 'EMESSO', color: 'bg-slate-200 text-slate-800' },
  sent_to_client: { label: 'INVIATO AL CLIENTE', color: 'bg-blue-100 text-blue-800' },
  approved: { label: 'APPROVATO', color: 'bg-emerald-100 text-emerald-800' },
  approved_with_reservations: { label: 'APPROVATO CON RISERVE', color: 'bg-amber-100 text-amber-800' },
  rejected: { label: 'NON APPROVATO', color: 'bg-rose-100 text-rose-800' },
  invoiced: { label: 'FATTURATO', color: 'bg-purple-100 text-purple-800' },
  superseded: { label: 'SOSTITUITO', color: 'bg-slate-100 text-slate-500 line-through' },
};

const fmt = (v) => `€ ${Number(v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const auth = () => {
  const t = localStorage.getItem('access_token') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function SalApprovalPanel({ projectId, salReports = [], project }) {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emitting, setEmitting] = useState(null);
  const [sendDialog, setSendDialog] = useState(null); // approval object
  const [sendForm, setSendForm] = useState({ signer_email: '', signer_name: '', signer_phone: '', cc_emails: '', expires_in_days: 7, custom_message: '' });
  const [sendBusy, setSendBusy] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/projects/${projectId}/sal-approvals`, {
        headers: { ...auth() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setApprovals(j.items || []);
    } catch (e) {
      toast.error(`Errore caricamento approvals: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleEmit = async (salReportId) => {
    const dlName = project?.director_of_works_name_snapshot;
    if (!dlName) {
      return toast.error("Direttore Lavori mancante. Compilarlo prima di emettere l'approvazione.");
    }
    setEmitting(salReportId);
    try {
      const res = await fetch(`${BASE}/api/projects/${projectId}/sal-reports/${salReportId}/emit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth() },
      });
      const j = await res.json();
      if (!res.ok) {
        if (j?.detail?.code === 'PDF_NOT_CACHED') {
          throw new Error("Rigenera il Report SAL prima di emetterlo (cache PDF scaduta)");
        }
        if (j?.detail?.code === 'DIRECTOR_OF_WORKS_REQUIRED') {
          throw new Error('Direttore Lavori obbligatorio');
        }
        throw new Error(j?.detail?.message || j?.detail || `HTTP ${res.status}`);
      }
      toast.success(`Emesso v${j.version} · SHA-256 ${j.issued_pdf_sha256.slice(0, 12)}…`);
      load();
    } catch (e) {
      toast.error(`Emissione fallita: ${e.message}`);
    } finally {
      setEmitting(null);
    }
  };

  const openSendDialog = (a) => {
    const clientEmail = a?.client_snapshot?.email || '';
    const clientName = a?.client_snapshot?.name || '';
    setSendForm({
      signer_email: a?.signer_email || clientEmail,
      signer_name: a?.signer_name || clientName,
      signer_phone: a?.signer_phone || '',
      cc_emails: (a?.cc_recipients || []).join(', '),
      expires_in_days: 7,
      custom_message: '',
    });
    setSendDialog(a);
  };

  const handleSend = async () => {
    if (!sendDialog) return;
    const email = (sendForm.signer_email || '').trim();
    if (!email || !email.includes('@')) return toast.error('Email firmatario non valida');
    setSendBusy(true);
    try {
      const body = {
        signer_email: email,
        signer_name: sendForm.signer_name?.trim() || null,
        signer_phone: sendForm.signer_phone?.trim() || null,
        cc_emails: (sendForm.cc_emails || '').split(',').map(e => e.trim()).filter(e => e && e.includes('@')),
        expires_in_days: Math.max(1, Math.min(30, Number(sendForm.expires_in_days) || 7)),
        custom_message: sendForm.custom_message?.trim() || null,
      };
      const res = await fetch(`${BASE}/api/sal-approvals/${sendDialog.id}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j?.detail || `HTTP ${res.status}`);
      toast.success('Link firma inviato');
      setSendDialog(null);
      load();
    } catch (e) {
      toast.error(`Invio fallito: ${e.message}`);
    } finally {
      setSendBusy(false);
    }
  };

  const handleRevoke = async (a) => {
    if (!window.confirm('Revocare il link di firma? Il cliente dovrà ricevere un nuovo link.')) return;
    try {
      const res = await fetch(`${BASE}/api/sal-approvals/${a.id}/revoke-token`, {
        method: 'POST',
        headers: { ...auth() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Token revocato');
      load();
    } catch (e) {
      toast.error(`Revoca fallita: ${e.message}`);
    }
  };

  const download = (a, kind = 'issued') => {
    const url = `${BASE}/api/sal-approvals/${a.id}/${kind}-pdf`;
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then(blob => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `SAL_${a.sal_number}_v${a.version}_${kind}.pdf`;
        link.click();
      })
      .catch(e => toast.error(`Download fallito: ${e.message}`));
  };

  return (
    <Card data-testid="sal-approval-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="w-5 h-5 text-slate-700" />
          Approvazione Cliente (R.6.1)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm text-slate-600 mb-2">Report SAL disponibili per emissione:</p>
          <div className="space-y-2">
            {(salReports || []).length === 0 && (
              <p className="text-xs text-slate-500 italic">Nessun report SAL generato ancora.</p>
            )}
            {(salReports || []).map(sr => {
              const emitted = approvals.find(a => a.sal_report_id === sr.id && a.status !== 'superseded');
              return (
                <div key={sr.id} className="flex items-center justify-between gap-3 p-3 border rounded bg-slate-50">
                  <div className="flex-1">
                    <div className="text-sm font-medium">{sr.report_number || sr.id.slice(0,8)}</div>
                    <div className="text-xs text-slate-500">Avanzamento: {Number(sr.sal_percentage || 0).toFixed(1)}%</div>
                  </div>
                  {emitted ? (
                    <Badge className={STATUS_LABELS[emitted.status]?.color || ''}>
                      {STATUS_LABELS[emitted.status]?.label || emitted.status} · v{emitted.version}
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => handleEmit(sr.id)}
                      disabled={emitting === sr.id}
                      data-testid={`emit-approval-${sr.id}`}
                    >
                      <Signature className="w-4 h-4 mr-1" />
                      {emitting === sr.id ? 'Emissione…' : 'Emetti per firma'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {approvals.length > 0 && (
          <div>
            <p className="text-sm text-slate-600 mb-2">Approvazioni ({approvals.length}):</p>
            <div className="space-y-2" data-testid="sal-approvals-list">
              {approvals.map(a => {
                const s = STATUS_LABELS[a.status] || { label: a.status, color: '' };
                const canSend = ['emitted', 'sent_to_client'].includes(a.status);
                const canRevoke = a.status === 'sent_to_client' && !a.public_token_revoked;
                return (
                  <div key={a.id} className="p-3 border rounded" data-testid={`approval-${a.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium">
                          {a.sal_number} · v{a.version}
                          <Badge className={`ml-2 ${s.color}`}>{s.label}</Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          Emesso: {a.emitted_at ? new Date(a.emitted_at).toLocaleString('it-IT') : '—'}
                          {a.signer_email && <> · Firmatario: {a.signer_email}</>}
                        </div>
                        {a.issued_pdf_sha256 && (
                          <div className="text-[10px] text-slate-400 font-mono mt-1 break-all">
                            issued sha256: {a.issued_pdf_sha256}
                          </div>
                        )}
                        {a.outcome && (
                          <div className="mt-2 text-sm">
                            {a.outcome === 'approved' && <span className="text-emerald-700 font-medium">Approvato · {fmt(a.authorized_amount)}</span>}
                            {a.outcome === 'approved_with_reservations' && <span className="text-amber-700 font-medium">Approvato con riserve · {fmt(a.authorized_amount)}</span>}
                            {a.outcome === 'rejected' && <span className="text-rose-700 font-medium">Non approvato</span>}
                            {a.reservations && <div className="text-xs text-slate-600 mt-1">Riserve: {a.reservations}</div>}
                            {a.rejection_reason && <div className="text-xs text-slate-600 mt-1">Motivazione: {a.rejection_reason}</div>}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {canSend && (
                          <Button size="sm" variant="outline" onClick={() => openSendDialog(a)} data-testid={`send-approval-${a.id}`}>
                            <Send className="w-4 h-4 mr-1" /> {a.status === 'sent_to_client' ? 'Reinvia' : 'Invia al cliente'}
                          </Button>
                        )}
                        {canRevoke && (
                          <Button size="sm" variant="outline" onClick={() => handleRevoke(a)} data-testid={`revoke-approval-${a.id}`}>
                            <Ban className="w-4 h-4 mr-1" /> Revoca link
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => download(a, 'issued')} data-testid={`download-issued-${a.id}`}>
                          <Download className="w-4 h-4 mr-1" /> Issued
                        </Button>
                        {a.signed_pdf_r2_key && (
                          <Button size="sm" variant="outline" onClick={() => download(a, 'signed')} data-testid={`download-signed-${a.id}`}>
                            <Download className="w-4 h-4 mr-1" /> Signed
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-xs text-slate-500 pt-3 border-t flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            Sottoscrizione elettronica con identificazione tramite OTP email e audit trail.
            Il PDF <strong>issued.pdf</strong> è immutabile: la sottoscrizione produce un
            <strong> signed.pdf</strong> con pagina evidenza aggiunta.
          </div>
        </div>
      </CardContent>

      <Dialog open={!!sendDialog} onOpenChange={(o) => !o && setSendDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="send-approval-dialog">
          <DialogHeader>
            <DialogTitle>Invia link firma al cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email firmatario *</Label>
              <Input
                type="email"
                value={sendForm.signer_email}
                onChange={(e) => setSendForm({ ...sendForm, signer_email: e.target.value })}
                data-testid="send-form-email"
              />
              <p className="text-xs text-slate-500 mt-1">L'anagrafica cliente NON viene modificata.</p>
            </div>
            <div>
              <Label>Nome firmatario</Label>
              <Input
                value={sendForm.signer_name}
                onChange={(e) => setSendForm({ ...sendForm, signer_name: e.target.value })}
                data-testid="send-form-name"
              />
            </div>
            <div>
              <Label>Telefono firmatario (opzionale)</Label>
              <Input
                value={sendForm.signer_phone}
                onChange={(e) => setSendForm({ ...sendForm, signer_phone: e.target.value })}
                data-testid="send-form-phone"
              />
            </div>
            <div>
              <Label>Destinatari in copia (opzionali, separati da virgola)</Label>
              <Input
                value={sendForm.cc_emails}
                onChange={(e) => setSendForm({ ...sendForm, cc_emails: e.target.value })}
                placeholder="tecnico@example.com, altro@example.com"
                data-testid="send-form-cc"
              />
              <p className="text-xs text-slate-500 mt-1">I destinatari in copia NON possono sottoscrivere.</p>
            </div>
            <div>
              <Label>Scadenza link (giorni)</Label>
              <Input
                type="number" min="1" max="30"
                value={sendForm.expires_in_days}
                onChange={(e) => setSendForm({ ...sendForm, expires_in_days: e.target.value })}
                data-testid="send-form-expires"
              />
            </div>
            <div>
              <Label>Messaggio personalizzato (opzionale)</Label>
              <Textarea
                rows={3}
                value={sendForm.custom_message}
                onChange={(e) => setSendForm({ ...sendForm, custom_message: e.target.value })}
                data-testid="send-form-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(null)}>Annulla</Button>
            <Button onClick={handleSend} disabled={sendBusy} data-testid="send-form-submit">
              <Send className="w-4 h-4 mr-1" />
              {sendBusy ? 'Invio…' : 'Invia link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
